import SignalType from "app/pages/sales-intelligence/sub-modules/signals/models/core/SignalType";
import type { EcommerceSignalMetricChangeDirection } from "app/pages/sales-intelligence/sub-modules/signals/models/ecommerce/EcommerceSignalMetricChangeDirection";
import { isEcommerceSignalMetricChangeDirection } from "app/pages/sales-intelligence/sub-modules/signals/models/ecommerce/type-guards/is-ecommerce-signal-metric-change-direction";
import { ecommerceSignalMetricChangeDirectionToTagMap } from "app/pages/sales-intelligence/sub-modules/signals/models/ecommerce/ecommerceSignalMetricChangeDirectionToTagMap";
import { isTrafficChangesModel } from "app/pages/sales-intelligence/sub-modules/signals/models/traffic/type-guards/isTrafficChangesModel";
import { isTrafficMarketModel } from "app/pages/sales-intelligence/sub-modules/signals/models/traffic/type-guards/isTrafficMarketModel";

import type {
    EcommerceSignalEventDto,
    NewsSignalEventDto,
    SignalEventDto,
    TechnologySignalEventDto,
    TrafficSignalEventDto,
} from "../../../types/signals";
import { newsSignalEventsTagsMap } from "../maps/newsSignalEventsTagsMap";
import { technologySubTypeToTagMap, technologyTypeToTagMap } from "../maps/technologySignalEventsTagsMap";
import { NEW_MARKETS_TAG, trafficChangesDirectionToTagMap } from "../maps/trafficSignalEventsTagsMap";

export type GetSignalEventTagsOptions = {
    /**
     * When true, returns no tags for types that already render tags in `SignalEventInfoBox`
     * (card layout), so the footer tag strip does not duplicate them.
     */
    omitTypesRenderedInInfoBox?: boolean;
};

const signalTypesWithTagsInInfoBox: ReadonlySet<SignalType> = new Set([
    SignalType.Technology,
    SignalType.Ecommerce,
]);

const getNewsSignalEventTags = (signal: NewsSignalEventDto): string[] => {
    const { categories } = signal;
    return categories
        .map((category) => (newsSignalEventsTagsMap[category] ? newsSignalEventsTagsMap[category] : []))
        .flat();
};

const getTechnologySignalEventTags = (signal: TechnologySignalEventDto): string[] => {
    const { changeType, subType } = signal;
    const firstTag = technologyTypeToTagMap[changeType] || "";
    if (!subType) {
        return [firstTag];
    } else {
        const secondTag = technologySubTypeToTagMap[subType] || "";
        return [firstTag, secondTag];
    }
};

const getTrafficSignalEventTags = (signal: TrafficSignalEventDto): string[] => {
    const { filters } = signal;

    if (isTrafficChangesModel(filters.trafficChanges)) {
        const { trafficChanges } = filters;

        const tag = trafficChangesDirectionToTagMap[trafficChanges.direction];

        return tag ? [tag] : [];
    }

    if (isTrafficMarketModel(filters.newMarkets)) {
        return [NEW_MARKETS_TAG];
    }

    return [];
};

function resolveEcommerceSignalEventDirection(
    signal: EcommerceSignalEventDto,
): EcommerceSignalMetricChangeDirection | undefined {
    const candidates = [signal.direction, signal.filters.change?.direction, signal.filters.direction];

    for (const candidate of candidates) {
        if (typeof candidate === "string" && isEcommerceSignalMetricChangeDirection(candidate)) {
            return candidate;
        }
    }

    return undefined;
}

const getEcommerceSignalEventTags = (signal: EcommerceSignalEventDto): string[] => {
    const direction = resolveEcommerceSignalEventDirection(signal);

    if (direction == null) {
        return [];
    }

    const tag = ecommerceSignalMetricChangeDirectionToTagMap[direction];
    return tag ? [tag] : [];
};

const getSignalEventTagsByType = (signal: SignalEventDto, options?: GetSignalEventTagsOptions): string[] => {
    const { type } = signal;

    if (options?.omitTypesRenderedInInfoBox && signalTypesWithTagsInInfoBox.has(type)) {
        return [];
    }

    if (type === SignalType.News) {
        return getNewsSignalEventTags(signal);
    } else if (type === SignalType.Technology) {
        return getTechnologySignalEventTags(signal);
    } else if (type === SignalType.Traffic) {
        return getTrafficSignalEventTags(signal);
    } else if (type === SignalType.Ecommerce) {
        return getEcommerceSignalEventTags(signal);
    }
    return [];
};

const getSignalEventTags = (signal: SignalEventDto, options?: GetSignalEventTagsOptions): string[] =>
    getSignalEventTagsByType(signal, options).filter((tag) => !!tag);

export default getSignalEventTags;
